import { Box, Typography, Link } from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import relayLogo from '../../assets/logo/relay-logo.png';
import FieldRenderer, { BenefitIcon, UnverifiedAddressCard, VerifiedAddressCard } from './FieldRenderer';
import WarningBox from './WarningBox';
import { ONLINE_CART_TOKENS } from './style';
import { interpolateTemplate, tokenizeTemplate } from './templateUtils';
import { useRef } from 'react';
import { areAddressesDifferent } from './validation';

function normalizeFields(screen) {
    if (!screen) {
        return [];
    }
    if (Array.isArray(screen.fields)) {
        return screen.fields;
    }
    if (screen.field) {
        return [screen.field];
    }
    return [];
}

function resolveFieldValue(field, answers = {}) {
    if (field?.id === 'stateOfFormation' && answers?.stateOfFormation && typeof answers.stateOfFormation === 'object') {
        return answers.stateOfFormation.stateCode || '';
    }
    return answers?.[field?.id] || '';
}

export default function QuestionRenderer({ screen, answers, errors, onChange, onNext, onProceedWithCurrentAddress, onUseVerifiedAddress, onKeepMine, proceedLoading }) {
    const fields = normalizeFields(screen);
    const inputFields = fields.filter((field) => field.type !== 'checkbox');
    const checkboxFields = fields.filter((field) => field.type === 'checkbox');
    const hasSplitTitle = screen?.titleHighlight || screen?.titlePrefix || screen?.titleSuffix;
    const isMailingAddressScreen = screen?.id === 'mailing_address';
    const isOrderSummaryScreen = screen?.id === 'order_summary';

    // Hide the "we found a verified USPS address" card when the saved address already matches
    // the suggestion under the merged comparison (e.g. unit in line2 vs Smarty's combined line1).
    const suggestedMailingAddress = answers?.smartySuggestedMalingAddress;
    const enteredMailingMatchesSuggested = Boolean(suggestedMailingAddress) && !areAddressesDifferent({
        addressLine1: answers?.mailingAddress1,
        addressLine2: answers?.mailingAddress2,
        city: answers?.mailingCity,
        stateCode: answers?.mailingStateCode,
        zipCode: answers?.mailingZip,
    }, suggestedMailingAddress);

    const businessDisplayName = `${answers?.businessName?.trim() || 'Your business'} LLC`;

    const inputFieldsContainerRef = useRef(null);

    const templateValues = {
        ...answers,
        businessName: businessDisplayName,
    };
    const resolvedTitlePrefix = interpolateTemplate(screen?.titlePrefix, templateValues);
    const resolvedTitleHighlight = interpolateTemplate(screen?.titleHighlight, templateValues);
    const resolvedTitleSuffix = interpolateTemplate(screen?.titleSuffix, templateValues);
    const titleTokens = tokenizeTemplate(screen?.title, templateValues);

    if (!screen) {
        return null;
    }

    return (
        <Box
            sx={{
                maxWidth: isOrderSummaryScreen ? 800 : ONLINE_CART_TOKENS.questionMaxWidth,
                mx: 'auto',
                fontFamily: ONLINE_CART_TOKENS.fontFamily,
            }}
        >
            {/* Logo, icons */}
            {screen.heroLogo === 'relay' ? (
                <Box
                    component="img"
                    src={screen.heroLogoUrl || relayLogo}
                    alt="Relay"
                    sx={{
                        height: { xs: 34, sm: 42 },
                        width: 'auto',
                        display: 'block',
                        mb: 3,
                    }}
                />
            ) : null}

            {screen.heroIcon === 'shield' || screen.heroIcon === 'zap' ? (
                <Box
                    sx={{
                        width: 66,
                        height: 66,
                        borderRadius: 2.5,
                        bgcolor: screen.heroIcon === 'zap' ? ONLINE_CART_TOKENS.zapBg : ONLINE_CART_TOKENS.shieldBg,
                        display: 'grid',
                        placeItems: 'center',
                        mb: 3,
                    }}
                >
                    {screen.heroIcon === 'zap' ? (
                        <BoltOutlinedIcon sx={{ fontSize: 34, color: ONLINE_CART_TOKENS.accentOrange }} />
                    ) : (
                        <ShieldOutlinedIcon sx={{ fontSize: 34, color: ONLINE_CART_TOKENS.primary }} />
                    )}
                </Box>
            ) : null}

            {/* Question title */}
            <Typography
                variant="h3"
                sx={{
                    fontSize: { xs: '1.5rem', sm: '1.875rem' },
                    lineHeight: 1.2,
                    fontWeight: 700,
                    color: ONLINE_CART_TOKENS.textDisplay,
                    letterSpacing: '-0.02em',
                }}
            >
                {hasSplitTitle ? (
                    <>
                        {resolvedTitlePrefix || ''}
                        {resolvedTitleHighlight ? (
                            <Typography component="span" sx={{ color: ONLINE_CART_TOKENS.primaryStrong, fontWeight: 800, fontSize: 'inherit' }}>
                                {resolvedTitleHighlight}
                            </Typography>
                        ) : null}
                        {resolvedTitleSuffix || ''}
                    </>
                ) : (
                    titleTokens.map((part) => {
                        if (part.type === 'token' && part.tokenPath === 'businessName') {
                            return (
                                <Typography key={part.key} component="span" sx={{ color: ONLINE_CART_TOKENS.primaryAccent, fontWeight: 700, fontSize: 'inherit' }}>
                                    {part.value}
                                </Typography>
                            );
                        }

                        return (
                            <Typography key={part.key} component="span" sx={{ color: 'inherit', fontWeight: 700, fontSize: 'inherit' }}>
                                {part.value}
                            </Typography>
                        );
                    })
                )}
            </Typography>

            {/* Question descrpption */}
            {screen.description && (
                <Typography
                    sx={{
                        mt: 1,
                        fontSize: isMailingAddressScreen ? '0.875rem' : '1rem',
                        lineHeight: 1.45,
                        color: ONLINE_CART_TOKENS.textSupport,
                    }}
                >
                    {isOrderSummaryScreen ? (
                        <>
                            <Typography component="span" sx={{ color: ONLINE_CART_TOKENS.primary, fontWeight: 700, fontSize: 'inherit' }}>
                                {`${businessDisplayName}`}
                            </Typography>{' '}
                            is almost official!
                        </>
                    ) : (
                        screen.description
                    )}
                </Typography>
            )}

            {screen.benefits && screen.benefitsMode === 'always' ? (
                <Box sx={{ mt: 2.5, display: 'grid', rowGap: 2 }}>
                    {(screen.benefits || []).map((item, index) => (
                        <Box key={`${item.text}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: ONLINE_CART_TOKENS.successBgSoft,
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <BenefitIcon type={item.icon} />
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>{item.text}</Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}

            {inputFields.length > 0 && (
                <Box
                    sx={{
                        mt: isMailingAddressScreen ? 3 : 4,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                        columnGap: 2,
                        rowGap: isMailingAddressScreen ? 2 : 2.25,
                    }}
                >
                    {inputFields.map((field, index) => (
                        <Box
                            key={field.id}
                            ref={inputFieldsContainerRef}
                            data-online-cart-field={field.id}
                            sx={{
                                gridColumn: {
                                    xs: 'span 12',
                                    sm: `span ${field.columnSpanSm || 12}`,
                                },
                            }}
                        >
                            <FieldRenderer
                                field={field}
                                value={resolveFieldValue(field, answers)}
                                error={errors[field.id] || ''}
                                onChange={(value) => onChange(field.id, value)}
                                onFieldChange={onChange}
                                allAnswers={answers}
                                onNext={field.type === 'orderSummary' ? onNext : undefined}
                                autoFocus={false}
                                refs={{
                                    inputFieldsContainerRef,
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {checkboxFields.length > 0 && (
                <Box sx={{ mt: 4, display: 'grid', rowGap: 1.75 }}>
                    {checkboxFields.map((field, index) => (
                        <Box key={field.id} data-online-cart-field={field.id}>
                            <FieldRenderer
                                field={field}
                                value={answers[field.id] || false}
                                error={errors[field.id] || ''}
                                onChange={(value) => onChange(field.id, value)}
                                onFieldChange={onChange}
                                allAnswers={answers}
                                onNext={field.type === 'orderSummary' ? onNext : undefined}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {screen.sharedDisclosure && screen.sharedDisclosure.text ? (
                <Typography sx={{ mt: 1, fontSize: '0.75rem', lineHeight: 1.6, color: ONLINE_CART_TOKENS.textMuted, pl: '2.25rem' }}>
                    {screen.sharedDisclosure.text}
                    {(screen.sharedDisclosure.links || []).map((link, index) => (
                        <Box component="span" key={link.href}>
                            <Link
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="always"
                                sx={{
                                    fontSize: '0.75rem',
                                    lineHeight: 1.6,
                                    color: ONLINE_CART_TOKENS.textInk,
                                    fontWeight: 400,
                                    textDecorationColor: ONLINE_CART_TOKENS.textInk,
                                    '&:visited': {
                                        color: ONLINE_CART_TOKENS.textInk,
                                        textDecorationColor: ONLINE_CART_TOKENS.textInk,
                                    },
                                    '&:hover': {
                                        color: ONLINE_CART_TOKENS.textInk,
                                        textDecorationColor: ONLINE_CART_TOKENS.textInk,
                                    },
                                }}
                            >
                                {link.label}
                            </Link>
                            {index < screen.sharedDisclosure.links.length - 1 ? ' and ' : ''}
                        </Box>
                    ))}
                    {screen.sharedDisclosure.suffix || ''}
                </Typography>
            ) : null}

            <WarningBox warning={screen.warning} />

            {isMailingAddressScreen && (
                <>
                    <Box data-online-cart-error-screen={screen?.id} mt={1.5}>
                        <UnverifiedAddressCard
                            message={errors?.mailingAddress || (answers?.mailingAddressOverride?.overridden ? answers?.mailingAddressOverride?.message : '')}
                            proceeded={!errors?.mailingAddress && Boolean(answers?.mailingAddressOverride?.overridden)}
                            address={{
                                addressLine1: answers?.mailingAddress1,
                                addressLine2: answers?.mailingAddress2,
                                city: answers?.mailingCity,
                                stateCode: answers?.mailingStateCode,
                                zipCode: answers?.mailingZip,
                            }}
                            onProceed={onProceedWithCurrentAddress}
                            loading={proceedLoading}
                        />
                    </Box>
                    {!enteredMailingMatchesSuggested && (
                        <VerifiedAddressCard
                            address={answers?.smartySuggestedMalingAddress}
                            onUseVerifiedAddress={onUseVerifiedAddress}
                            onKeepMine={onKeepMine}
                            loading={proceedLoading}
                        />
                    )}
                </>
            )}
        </Box>
    );
}
